import type { JobClickTrackingSelectedJobAttributes } from '../../../../context/ViewJobDataContext';

/**
 * Web implementation of legacy auto-open job-click tracking, ported from
 * jobsearch-ui `jasxJobClick.tsx` (`triggerJobClickTracking` /
 * `triggerRawClickLogging`). Fired from the RNVJ ApplyButton when the viewjob was
 * auto-opened in a desktop two-pane feed (the user never clicked the job card),
 * so the job click is counted at apply time.
 *
 * Native is a no-op (see `jobClickTracking.ts`).
 */

function createImgBeacon(url: string): void {
    const imgBeacon = new globalThis.Image();
    imgBeacon.src = url;
}

/**
 * Pushes a history entry updating `vjk`/`advn` to reflect the selected job,
 * mirroring legacy `generateNewHistoryUrlParams` + `pushState`.
 */
function pushSelectedJobHistory(
    selectedJobAttributes: JobClickTrackingSelectedJobAttributes,
): void {
    try {
        const currentUrl = new URL(window.location.href);
        const params = currentUrl.searchParams;
        if (selectedJobAttributes.advn) {
            params.set('advn', selectedJobAttributes.advn);
        } else {
            params.delete('advn');
        }
        if (selectedJobAttributes.jobKey) {
            params.set('vjk', selectedJobAttributes.jobKey);
        }
        params.delete('indconvtk');
        window.history.pushState(null, '', currentUrl.href);
    } catch (error) {
        console.error('[jobClickTracking] Failed to push selected job history:', error);
    }
}

/**
 * Builds the internal click-tracking URL: adds `vjs=2`, `autoopen=1`, and a
 * cache-busting `__` param. Mirrors legacy `getInternalTrackingURL`. This util
 * is only invoked for auto-opened jobs, so `autoopen=1` is always set.
 */
function getInternalTrackingURL(internalTrackUrl: URL): string {
    internalTrackUrl.searchParams.set('vjs', '2');
    internalTrackUrl.searchParams.set('autoopen', '1');
    internalTrackUrl.searchParams.append('__', Math.random().toString());

    return internalTrackUrl.href;
}

function triggerExternalTracking(trackingUrls: string[] | null | undefined): void {
    if (!trackingUrls) {
        return;
    }
    trackingUrls.forEach((url: string) => {
        try {
            const externalTrackingUrl = new URL(url);
            externalTrackingUrl.searchParams.append('__', Math.random().toString());
            createImgBeacon(externalTrackingUrl.href);
        } catch (error) {
            console.error('[jobClickTracking] Failed to fire external tracking beacon:', error);
        }
    });
}

export function triggerJobClickTracking(
    selectedJobAttributes: JobClickTrackingSelectedJobAttributes | undefined,
): void {
    if (!selectedJobAttributes || !selectedJobAttributes.link) {
        return;
    }
    pushSelectedJobHistory(selectedJobAttributes);
    try {
        const baseInternalTrackingUrl = new URL(
            selectedJobAttributes.link,
            window.location.origin,
        );
        createImgBeacon(getInternalTrackingURL(baseInternalTrackingUrl));
    } catch (error) {
        console.error('[jobClickTracking] Failed to fire internal tracking beacon:', error);
    }
    triggerExternalTracking(selectedJobAttributes.extractTrackingUrls);
}

export function triggerRawClickLogging(
    selectedJobAttributes: JobClickTrackingSelectedJobAttributes | undefined,
    isLoggedIn: boolean,
): void {
    if (!selectedJobAttributes) {
        return;
    }
    try {
        const rawClickLoggingURL = new URL('/m/rpc/rawclick', window.location.origin);
        rawClickLoggingURL.searchParams.set('tk', selectedJobAttributes.tk ?? '');
        rawClickLoggingURL.searchParams.set('jobKey', selectedJobAttributes.jobKey ?? '');
        rawClickLoggingURL.searchParams.set('sponsored', selectedJobAttributes.adId ? '1' : '0');
        rawClickLoggingURL.searchParams.set('loggedIn', isLoggedIn ? '1' : '0');
        void fetch(rawClickLoggingURL.toString());
    } catch (error) {
        console.error('[jobClickTracking] Failed to fire raw click logging:', error);
    }
}
